import * as React from 'react';

import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { automationActivateAtom } from '../_graphql/mutationAutomationActivate';
import { automationBulkActivateAtom } from '../_graphql/mutationAutomationBulkActivate';
import { automationBulkDeleteAtom } from '../_graphql/mutationAutomationBulkDelete';
import { automationBulkPauseAtom } from '../_graphql/mutationAutomationBulkPause';
import { automationDeleteAtom } from '../_graphql/mutationAutomationDelete';
import { automationDuplicateAtom } from '../_graphql/mutationAutomationDuplicate';
import { automationPauseAtom } from '../_graphql/mutationAutomationPause';
import { automationsListQueryAtom } from '../_graphql/queryAutomations';
import { AutomationStatus, TriggerType, AutomationSortOption } from '../types';

import { atomFromQueryString } from '~/shared/state/location.state';

// Query String Parameters
export const $searchTerm = atomFromQueryString.string('q');
export const $selectedStatus =
  atomFromQueryString.string<AutomationStatus>('status');
export const $selectedTriggerType =
  atomFromQueryString.string<TriggerType>('trigger');
export const $sortOption = atomFromQueryString.string<AutomationSortOption>(
  'sort',
  'UpdatedAtDesc',
);
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);

// Computed Data - Query Params
export const $automationsListParams = atom((get) => {
  const searchTerm = get($searchTerm);
  const selectedStatus = get($selectedStatus);
  const selectedTriggerType = get($selectedTriggerType);
  const sortOption = get($sortOption);
  const limit = get($limit);
  const offset = get($offset);

  const params: {
    searchTerm?: string;
    status?: AutomationStatus[];
    triggerType?: TriggerType[];
    sorting?: AutomationSortOption[];
    limit: number;
    offset: number;
  } = {
    limit,
    offset,
  };

  if (searchTerm?.trim()) params.searchTerm = searchTerm;
  if (selectedStatus) params.status = [selectedStatus];
  if (selectedTriggerType) params.triggerType = [selectedTriggerType];
  if (sortOption) params.sorting = [sortOption];

  return params;
});

// Remote Data
export const $remoteAutomations = automationsListQueryAtom((get) => ({
  variables: get($automationsListParams),
}));

// Selection State
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());

// Pagination Helpers (computed from query string params)
export const $currentPage = atom((get) => {
  const offset = get($offset);
  const limit = get($limit);
  return Math.floor(offset / limit) + 1;
});

export const $totalPages = atom((get) => {
  const data = get($remoteAutomations).data;
  const limit = get($limit);
  const total = data?.total ?? 0;
  return Math.ceil(total / limit);
});

// Actions
export const $deleteAutomation = automationDeleteAtom({
  onSuccess: (get) => {
    get($remoteAutomations).refetch?.();
  },
});

export const $activateAutomation = automationActivateAtom({
  onSuccess: (get) => {
    get($remoteAutomations).refetch?.();
  },
});

export const $pauseAutomation = automationPauseAtom({
  onSuccess: (get) => {
    get($remoteAutomations).refetch?.();
  },
});

export const $duplicateAutomation = automationDuplicateAtom({
  onSuccess: (get) => {
    get($remoteAutomations).refetch?.();
  },
});

export const $bulkActivateAutomation = automationBulkActivateAtom({
  onSuccess: (get) => {
    get($remoteAutomations).refetch?.();
  },
});

export const $bulkPauseAutomation = automationBulkPauseAtom({
  onSuccess: (get) => {
    get($remoteAutomations).refetch?.();
  },
});

export const $bulkDeleteAutomation = automationBulkDeleteAtom({
  onSuccess: (get) => {
    get($remoteAutomations).refetch?.();
  },
});
